import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Clock } from "lucide-react";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

type CustomTimePickerProps = {
  label?: string;
  showLabel?: boolean;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: any;
  height?: string | number;
  isRequired?: boolean;
  isDisabled?: boolean;
  minuteStep?: number;
};

const formatTimeLabel = (value: string) => {
  if (!value) return "";

  const [hourRaw, minuteRaw] = value.split(":");
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw || 0);

  if (Number.isNaN(hour)) return value;

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  const displayMinute = String(minute).padStart(2, "0");

  return `${displayHour}:${displayMinute} ${period}`;
};

const buildTimeOptions = (minuteStep: number) => {
  const options: { value: string; label: string }[] = [];

  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += minuteStep) {
      const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(
        2,
        "0",
      )}`;

      options.push({
        value,
        label: formatTimeLabel(value),
      });
    }
  }

  return options;
};

const CustomTimePicker: React.FC<CustomTimePickerProps> = ({
  label,
  showLabel = true,
  value,
  onChange,
  placeholder = "Select time",
  width = "100%",
  height = "42px",
  isRequired = false,
  isDisabled = false,
  minuteStep = 30,
}) => {
  const { theme } = useCustomTheme();

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const selectedOptionRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    left: 0,
    top: 0,
    width: 240,
  });

  const options = useMemo(() => buildTimeOptions(minuteStep), [minuteStep]);

  const selectedLabel = value ? formatTimeLabel(value) : "";

  const updateDropdownPosition = () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();

    setDropdownPosition({
      left: rect.left + window.scrollX,
      top: rect.bottom + window.scrollY + 6,
      width: rect.width,
    });
  };

  const openPicker = () => {
    if (isDisabled) return;

    updateDropdownPosition();
    setIsOpen(true);

    window.setTimeout(() => {
      selectedOptionRef.current?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }, 80);
  };

  const closePicker = () => {
    setIsOpen(false);
  };

  const togglePicker = () => {
    if (isDisabled) return;

    if (isOpen) {
      closePicker();
      return;
    }

    openPicker();
  };

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (wrapperRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;

      setIsOpen(false);
    };

    const handleResize = () => updateDropdownPosition();
    const handleScroll = () => updateDropdownPosition();

    document.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  return (
    <Box w={width} position="relative" ref={wrapperRef}>
      {showLabel && label && (
        <Flex
          alignItems="center"
          color={theme.textMuted}
          gap={2}
          mb={1}
          pl="2px"
        >
          <Text fontSize="11px" color={theme.textSubtle}>
            {label}
            {isRequired ? " *" : ""}
          </Text>
        </Flex>
      )}

      <InputGroup
        h={height}
        cursor={isDisabled ? "not-allowed" : "pointer"}
        onClick={togglePicker}
      >
        <Input
          h={height}
          value={selectedLabel}
          placeholder={placeholder}
          isReadOnly
          isDisabled={isDisabled}
          bg={theme.inputBg}
          color={theme.text}
          border="1px solid"
          borderColor={isOpen ? theme.inputBorderFocus : theme.inputBorder}
          borderRadius="md"
          fontWeight="600"
          cursor={isDisabled ? "not-allowed" : "pointer"}
          pr="42px"
          _placeholder={{ color: theme.inputPlaceholder }}
          _hover={{ borderColor: theme.inputBorderFocus }}
          _focus={{
            borderColor: theme.inputBorderFocus,
            boxShadow: `0 0 0 1px ${theme.inputBorderFocus}`,
          }}
        />

        <InputRightElement
          h={height}
          pointerEvents="none"
          color={isOpen ? theme.primary : theme.textSubtle}
        >
          <Clock size={16} />
        </InputRightElement>
      </InputGroup>

      {isOpen && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="absolute"
            zIndex={40000}
            left={`${dropdownPosition.left}px`}
            top={`${dropdownPosition.top}px`}
            width={`${dropdownPosition.width}px`}
            maxH="280px"
            overflowY="auto"
            bg={theme.surface}
            border="1px solid"
            borderColor={theme.borderStrong || theme.border}
            borderRadius="xl"
            boxShadow={theme.shadowMd}
            p={2}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            sx={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: theme.borderStrong || theme.border,
                borderRadius: "999px",
              },
            }}
          >
            <VStack align="stretch" spacing={1}>
              {options.map((option) => {
                const isSelected = option.value === value;

                return (
                  <Box
                    key={option.value}
                    ref={isSelected ? selectedOptionRef : undefined}
                    px={3}
                    py={2}
                    borderRadius="lg"
                    cursor="pointer"
                    color={isSelected ? theme.textOnPrimary : theme.text}
                    bg={
                      isSelected
                        ? theme.gradients?.primaryButton || theme.primary
                        : "transparent"
                    }
                    fontWeight={isSelected ? "800" : "600"}
                    fontSize="sm"
                    transition="all 0.18s ease"
                    _hover={{
                      bg: isSelected
                        ? theme.gradients?.primaryButtonHover || theme.primary
                        : theme.hover,
                      transform: "translateX(2px)",
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleSelect(option.value);
                    }}
                  >
                    {option.label}
                  </Box>
                );
              })}
            </VStack>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default CustomTimePicker;
